import type {
  Automation,
  AutomationCreate,
  AutomationList,
  AutomationRun,
} from "../../api/automations";
import { type Key, lang, t, tn } from "../../i18n";
import { clock, dayKey, formatDay } from "../../lib/time";
import { money, num, shortDate } from "../admin/memory/format";

// The Automations page's words and the New form's rules (PLAN §8.8; STATUS "R7 contract" 1–3,
// 11, 12), kept pure so they're tested on their own.

// ---- schedules: the New form's presets ↔ cron (company time, PLAN §8.8) -----------------------

export type Preset = "daily" | "weekdays" | "weekly" | "hourly" | "custom";

/** A preset schedule: `day` is cron's day of the week (0 = Sunday … 6 = Saturday), `time` is
 *  "HH:MM" (an hourly one uses only its minutes). */
export type Schedule = { preset: Exclude<Preset, "custom">; day: number; time: string };

const PRESETS: { value: Preset; key: Key }[] = [
  { value: "daily", key: "auto.presetDaily" },
  { value: "weekdays", key: "auto.presetWeekdays" },
  { value: "weekly", key: "auto.presetWeekly" },
  { value: "hourly", key: "auto.presetHourly" },
  { value: "custom", key: "auto.presetCustom" },
];

/** The presets, in the interface language. */
export function presets(): { value: Preset; label: string }[] {
  return PRESETS.map((p) => ({ value: p.value, label: t(p.key) }));
}

/** Monday first, as the week reads at the office. */
const DAYS: { value: number; key: Key }[] = [
  { value: 1, key: "auto.monday" },
  { value: 2, key: "auto.tuesday" },
  { value: 3, key: "auto.wednesday" },
  { value: 4, key: "auto.thursday" },
  { value: 5, key: "auto.friday" },
  { value: 6, key: "auto.saturday" },
  { value: 0, key: "auto.sunday" },
];

/** The days of the week, in the interface language. */
export function days(): { value: number; label: string }[] {
  return DAYS.map((d) => ({ value: d.value, label: t(d.key) }));
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** "09:30" → [9, 30]; anything else → null. */
export function splitTime(time: string): [number, number] | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return [h, min];
}

/** A preset → its cron: "0 9 * * *", "0 9 * * 1-5", "0 9 * * 1", "0 * * * *". */
export function presetCron(s: Schedule): string | null {
  const t = splitTime(s.time);
  if (!t) return null;
  const [h, m] = t;
  switch (s.preset) {
    case "daily":
      return `${m} ${h} * * *`;
    case "weekdays":
      return `${m} ${h} * * 1-5`;
    case "weekly":
      return `${m} ${h} * * ${((s.day % 7) + 7) % 7}`;
    case "hourly":
      return `${m} * * * *`;
  }
}

const NUM = /^\d{1,2}$/;

/** A cron → the preset it is, or null when it's none of them (the form shows "Custom"). An
 *  hourly one's hour is "00". */
export function parseCron(cron: string): Schedule | null {
  const f = cron.trim().split(/\s+/);
  if (f.length !== 5) return null;
  const [mi = "", ho = "", dom = "", mon = "", dow = ""] = f;
  if (!NUM.test(mi) || Number(mi) > 59 || dom !== "*" || mon !== "*") return null;
  const mm = pad(Number(mi));
  if (ho === "*") return dow === "*" ? { preset: "hourly", day: 1, time: `00:${mm}` } : null;
  if (!NUM.test(ho) || Number(ho) > 23) return null;
  const time = `${pad(Number(ho))}:${mm}`;
  if (dow === "*") return { preset: "daily", day: 1, time };
  if (dow === "1-5") return { preset: "weekdays", day: 1, time };
  if (/^[0-7]$/.test(dow)) return { preset: "weekly", day: Number(dow) % 7, time };
  return null;
}

// ---- events: what an automation can watch (R7 contract 5) -------------------------------------

export type EventSource = "redmine" | "github";

const EVENTS: { source: EventSource; value: string; key: Key }[] = [
  { source: "redmine", value: "issue_created", key: "auto.evIssueCreated" },
  { source: "redmine", value: "status_changed", key: "auto.evStatusChanged" },
  { source: "redmine", value: "assigned", key: "auto.evAssigned" },
  { source: "github", value: "pr_opened", key: "auto.evPrOpened" },
  { source: "github", value: "pr_merged", key: "auto.evPrMerged" },
  { source: "github", value: "review_requested", key: "auto.evReviewRequested" },
  { source: "github", value: "ci_failed", key: "auto.evCiFailed" },
];

export function eventsOf(source: EventSource): {
  source: EventSource;
  value: string;
  label: string;
}[] {
  return EVENTS.filter((e) => e.source === source).map((e) => ({
    source: e.source,
    value: e.value,
    label: t(e.key),
  }));
}

export function eventLabel(value: string): string {
  const e = EVENTS.find((x) => x.value === value);
  return e ? t(e.key) : value;
}

// ---- the list ---------------------------------------------------------------------------------

export type Tone = "ok" | "warn" | "danger" | "muted";

/** The status pill: Active, Paused, Needs attention; "On hold" while the spend hold lasts. */
export function statusOf(a: Pick<Automation, "status" | "held">): { label: string; tone: Tone } {
  if (a.status === "needs_attention")
    return { label: t("auto.statusNeedsAttention"), tone: "danger" };
  if (a.status === "paused" || a.status === "deleted")
    return { label: t("auto.statusPaused"), tone: "muted" };
  if (a.held) return { label: t("auto.statusOnHold"), tone: "warn" };
  return { label: t("auto.statusActive"), tone: "ok" };
}

const REASONS: Record<string, Key> = {
  manual: "auto.statusPaused",
  owner_left: "auto.reasonOwnerLeft",
  owner_deactivated: "auto.reasonOwnerDeactivated",
  team_archived: "auto.reasonTeamArchived",
};

/** Why it stopped, in words: "failures: <why>" → "Paused after 3 failed runs: <why>". */
export function reasonText(reason: string | null | undefined): string | null {
  if (!reason) return null;
  if (reason.startsWith("failures: "))
    return t("auto.reasonFailures", { why: reason.slice("failures: ".length) });
  const key = REASONS[reason];
  return key ? t(key) : null;
}

/** A row's muted second line: the instruction; why it stopped when it needs attention. */
export function sublineOf(a: Pick<Automation, "status" | "status_reason" | "instruction">) {
  if (a.status === "needs_attention") return reasonText(a.status_reason) ?? a.instruction;
  return a.instruction;
}

/** The kind icon (calendar for a schedule, zap for an event), red when it needs attention,
 *  muted when paused. */
export function kindOf(a: Pick<Automation, "trigger" | "status">) {
  const event = (a.trigger as { type?: string }).type === "event";
  const cls = a.status === "needs_attention" ? "danger" : a.status === "paused" ? "muted" : "";
  return {
    icon: event ? "i-zap" : "i-calendar",
    cls,
    label: event ? t("auto.triggered") : t("auto.timed"),
  };
}

export function isSchedule(a: Pick<Automation, "trigger">): boolean {
  return (a.trigger as { type?: string }).type === "schedule";
}

/** The list's When cell: the label without the company zone ("Every Mon 09:00"); the detail
 *  keeps it. */
export function whenShort(label: string): string {
  return label.replace(/\s*\(UTC[+-]\d{1,2}(?::\d{2})?\)$/, "");
}

/** Dollars as the header says them, always with cents (R10-S14): "$0.38", "$20.00". */
export function usd(x: number): string {
  return `$${x.toFixed(2)}`;
}

/** "6 automations · 4 active · $0.38 of the $20 monthly cap used". */
export function headerLine(
  l: Pick<AutomationList, "counts" | "month_cost_usd" | "monthly_cap_usd">,
): string {
  return t("auto.headerLine", {
    count: automationCount(l.counts.all),
    active: l.counts.active,
    spent: usd(l.month_cost_usd),
    cap: usd(l.monthly_cap_usd),
  });
}

/** The channel pill: "1 automation", "3 automations". */
export function automationCount(n: number): string {
  return tn("auto.count", n, { n: num(n) });
}

/** Posts to: the API's label, except my own inbox or email ("My inbox"). */
export function postsTo(dest: Automation["destination"], myId: string): string {
  if (dest.id && dest.id === myId) {
    if (dest.type === "inbox") return t("auto.myInbox");
    if (dest.type === "email") return t("auto.myEmail");
  }
  return dest.label;
}

/** "Fri Sep 25" ("Thứ 6, 25 thg 9" in Vietnamese) for a calendar day; `year` adds the year. */
function dayName(day: string, year = false): string {
  if (lang() === "vi")
    return formatDay(
      day,
      { weekday: "short", day: "numeric", month: "short", year: year ? "numeric" : undefined },
      "vi-VN",
    );
  const weekday = formatDay(day, { weekday: "short" }, "en-US");
  const month = formatDay(day, { month: "short" }, "en-US");
  const name = `${weekday} ${month} ${Number(day.slice(8, 10))}`;
  return year ? `${name} ${day.slice(0, 4)}` : name;
}

/** Last run: "Today 09:30", "Fri Sep 25", "Sep 25, 2025"; "—" for never. Days and times in the
 *  person's zone (R10-B2). */
export function lastRunLabel(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "—";
  const day = dayKey(iso);
  const current = dayKey(now);
  if (day === current) return t("auto.todayAt", { time: clock(iso) });
  if (day.slice(0, 4) === current.slice(0, 4)) return dayName(day);
  if (lang() === "vi")
    return formatDay(day, { day: "numeric", month: "short", year: "numeric" }, "vi-VN");
  return `${shortDate(day)}, ${day.slice(0, 4)}`;
}

/** A run's time: "Today 09:30", "Mon Sep 21, 09:30". */
export function runTime(iso: string, now: Date = new Date()): string {
  const day = dayKey(iso);
  const current = dayKey(now);
  if (day === current) return t("auto.todayAt", { time: clock(iso) });
  return `${dayName(day, day.slice(0, 4) !== current.slice(0, 4))}, ${clock(iso)}`;
}

// ---- the detail ---------------------------------------------------------------------------------

/** "Dev · created by Minh in chat · Sep 21". */
export function createdLine(
  a: Pick<Automation, "owner" | "created_by" | "created_via" | "created_at">,
): string {
  const form = a.created_via === "form";
  const who = a.created_by?.name;
  return [
    a.owner.label,
    who
      ? t(form ? "auto.createdByForm" : "auto.createdByChat", { name: who })
      : t(form ? "auto.createdForm" : "auto.createdChat"),
    shortDate(a.created_at),
  ]
    .filter(Boolean)
    .join(" · ");
}

/** "Under 1¢ a run · 2¢ so far" (the runs that called the model, and the total). */
export function costLine(
  total: number,
  runs: Pick<AutomationRun, "status" | "cost_usd" | "started_at">[],
  now: Date = new Date(),
) {
  const paid = runs.filter((r) => r.status === "ok" || (r.status === "failed" && r.cost_usd > 0));
  if (paid.length === 0 && total <= 0) {
    // Failed runs are runs (R10-K8): "3 failed · last Today 10:55", not "No runs yet".
    const failed = runs.filter((r) => r.status === "failed");
    const last = failed[0];
    if (!last) return t("auto.noRunsYet");
    return t("auto.failedRunsLine", { n: failed.length, time: runTime(last.started_at, now) });
  }
  const avg = paid.length ? paid.reduce((s, r) => s + r.cost_usd, 0) / paid.length : total;
  const per = avg < 0.01 ? t("auto.underCentPerRun") : t("auto.aboutPerRun", { cost: money(avg) });
  const so = total > 0 ? t("auto.spentSoFar", { cost: money(total) }) : t("auto.nothingSoFar");
  return `${per} · ${so}`;
}

/** "12 s", "2.5 min". */
export function duration(ms: number | null | undefined): string | null {
  if (!ms || ms <= 0) return null;
  if (ms < 90_000) return t("auto.seconds", { n: Math.max(1, Math.round(ms / 1000)) });
  return t("auto.minutes", { n: (ms / 60_000).toFixed(1).replace(/\.0$/, "") });
}

const PATHS: Record<string, Key> = {
  recipe: "auto.pathRecipe",
  full: "auto.pathFull",
  fallback: "auto.pathFallback",
};

const SKIPS: Record<string, Key> = {
  spend_cap: "auto.skipSpendCap",
  automations_off: "auto.skipAutomationsOff",
  paused: "auto.skipPaused",
  needs_attention: "auto.skipNeedsAttention",
  deleted: "auto.skipDeleted",
};

/** A run's state for its row. */
export function runState(
  r: Pick<AutomationRun, "status" | "error">,
): "ok" | "failed" | "skipped" | "open" {
  if (r.status === "ok") return "ok";
  if (r.status === "failed") return "failed";
  if (r.status === "skipped") return "skipped";
  return "open";
}

/** What follows a run's time: duration · cost · status · path ("12 s · under 1¢"). */
export function runMeta(
  r: Pick<AutomationRun, "status" | "error" | "duration_ms" | "cost_usd" | "path" | "kind">,
): string[] {
  const out: string[] = [];
  const state = runState(r);
  if (state === "open")
    return [r.status === "running" ? t("auto.running") : t("auto.waitingToRun")];
  const d = duration(r.duration_ms);
  if (d) out.push(d);
  if (r.cost_usd > 0) out.push(r.cost_usd < 0.01 ? t("auto.underCent") : money(r.cost_usd));
  if (r.kind === "manual") out.push(t("auto.runByHand"));
  if (state === "failed")
    out.push(r.error ? t("auto.failedWhy", { why: r.error }) : t("auto.failed"));
  if (state === "skipped") {
    const skip = SKIPS[r.error ?? ""];
    out.push(skip ? t(skip) : r.error ? t("auto.skippedWhy", { why: r.error }) : t("auto.skipped"));
  }
  const path = r.path ? PATHS[r.path] : undefined;
  if (state === "ok" && path) out.push(t(path));
  return out;
}

/** The guard rails, in one line. */
export function guardRails(): string {
  return t("auto.guardRails");
}

/** Who may change it, said to someone who can't. */
export function editorsOf(owner: Automation["owner"]): string {
  if (owner.type === "team") return t("auto.editorsTeam", { team: owner.label });
  if (owner.type === "person") return owner.label;
  return t("auto.editorsAdmin");
}

// ---- the New form -------------------------------------------------------------------------------

/** Posts to: a channel ("ch:<conversation id>"), my Inbox, or my email. */
export type DestChoice = "inbox" | "email" | `ch:${string}`;
/** Belongs to: a team ("team:<id>"), the company, or me. */
export type OwnerChoice = "person" | "company" | `team:${string}`;

/** The channel a destination points at, as the form knows it. */
export type ChannelInfo = { teamId: string | null; everyone: boolean; teamName: string | null };

export function ownerOptions(
  dest: DestChoice,
  channel: ChannelInfo | null,
  isAdmin: boolean,
): { value: OwnerChoice; label: string }[] {
  if (!dest.startsWith("ch:")) return [{ value: "person", label: t("auto.ownerMe") }];
  const out: { value: OwnerChoice; label: string }[] = [];
  if (channel?.teamId && !channel.everyone)
    out.push({
      value: `team:${channel.teamId}`,
      label: channel.teamName ?? t("auto.ownerTheTeam"),
    });
  if (isAdmin) out.push({ value: "company", label: t("auto.company") });
  out.push({ value: "person", label: t("auto.ownerMe") });
  return out;
}

/** Belongs to, from where it posts: a team channel → that team; #everyone → the company (for
 *  admins; anyone else's is their own); my Inbox or email → me. */
export function defaultOwner(
  dest: DestChoice,
  channel: ChannelInfo | null,
  isAdmin: boolean,
): OwnerChoice {
  if (!dest.startsWith("ch:") || !channel) return "person";
  if (channel.everyone) return isAdmin ? "company" : "person";
  return channel.teamId ? `team:${channel.teamId}` : "person";
}

export type Draft = {
  name: string;
  instruction: string;
  mode: "schedule" | "event";
  cron: string;
  source: EventSource;
  event: string;
  resource: string;
  toStatus: string;
  assignee: string;
  reviewer: string;
  dest: DestChoice;
  owner: OwnerChoice;
};

/** The form → `POST /api/automations` (the schedule in company time: no tz is sent). */
export function createBody(d: Draft): AutomationCreate {
  const body: AutomationCreate = {
    name: d.name.trim(),
    instruction: d.instruction.trim(),
    destination: d.dest.startsWith("ch:")
      ? { type: "channel", conversation_id: d.dest.slice(3) }
      : { type: d.dest === "email" ? "email" : "inbox" },
    owner:
      d.owner === "person"
        ? { type: "person" }
        : d.owner === "company"
          ? { type: "company" }
          : { type: "team", id: d.owner.slice("team:".length) },
  };
  if (d.mode === "schedule") {
    body.schedule = { cron: d.cron.trim().split(/\s+/).join(" ") };
  } else {
    const event: NonNullable<AutomationCreate["event"]> = {
      source: d.source,
      event: d.event,
      resource: d.resource.trim(),
    };
    if (d.event === "status_changed" && d.toStatus.trim()) event.to_status = d.toStatus.trim();
    if (d.event === "assigned" && d.assignee.trim()) event.assignee = d.assignee.trim();
    if (d.event === "review_requested" && d.reviewer.trim()) event.reviewer = d.reviewer.trim();
    body.event = event;
  }
  return body;
}

/** An automation's own post (a run's, or the one saying it paused itself), as opposed to the
 *  answer that set an automation up, which carries the live card (R7 contract 2, 6). */
export function isAutomationPost(m: { via: string; meta: unknown }): boolean {
  const meta = (m.meta ?? {}) as { run_kind?: unknown; sample?: unknown; paused?: unknown };
  return m.via === "automation" || Boolean(meta.run_kind || meta.sample || meta.paused);
}

/** The owner filter in the URL: mine, company or team:<id>. */
export function isOwnerFilter(value: unknown): value is string {
  return (
    typeof value === "string" &&
    (value === "mine" || value === "company" || /^team:[0-9a-fA-F-]{8,}$/.test(value))
  );
}
